// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { Moves } from '@gql/base-types';
import { Tooltip, Box, Typography } from '@mui/material';
import { getConfirmationSummary } from '@features/moves/utils/getConfirmationStatus';
import {
  requiresPickupConfirmation,
  requiresDeliveryConfirmation,
  hasPickupFlow,
  hasDeliveryFlow,
} from '@features/moves/utils/checkConfirmationRequirement';

// TYPES ---------------------------------------------------------------------- //

interface ConfirmationStatusTooltipProps {
  move?: Moves;
  children: React.ReactElement;
}

// COMPONENT ------------------------------------------------------------------ //

const ConfirmationStatusTooltip = ({ move, children }: ConfirmationStatusTooltipProps) => {
  // Only show tooltip for unconfirmed moves
  if (!move || move.status !== 'unconfirmed') {
    return children;
  }

  // Check if pickup or delivery requires confirmation
  let pickupRequired = requiresPickupConfirmation(move);
  let deliveryRequired = requiresDeliveryConfirmation(move);

  // Fallback: If workflowset.confirmationset is not loaded but move is unconfirmed,
  // try to get confirmationset from confirmationrequests
  if (!pickupRequired && !deliveryRequired) {
    const scenarioToMove = move.scenariotomoves?.[0];
    const confirmationsetFromRequests = scenarioToMove?.scenario?.confirmationrequests?.[0]?.confirmationset;

    if (confirmationsetFromRequests?.confirmation_flow) {
      // Use existing utility functions to check requirements
      pickupRequired = hasPickupFlow(confirmationsetFromRequests.confirmation_flow);
      deliveryRequired = hasDeliveryFlow(confirmationsetFromRequests.confirmation_flow);
    }
  }

  // Don't show tooltip if neither requires confirmation
  if (!pickupRequired && !deliveryRequired) {
    return children;
  }

  const confirmationSummary = getConfirmationSummary(move);

  // Format database status for display
  const formatStatusLabel = (status: string): string => {
    if (!status) return 'Pending';
    const normalized = status.replace(/_/g, ' ').toLowerCase();
    return normalized
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get color for database status
  const getStatusColor = (status: string): string => {
    if (!status) return '#9ca3af'; // gray

    const normalized = status.toLowerCase();
    switch (normalized) {
      case 'pending':
      case 'processing':
        return '#9ca3af'; // gray
      case 'sent':
        return '#3b82f6'; // blue
      case 'started':
        return '#a855f7'; // purple
      case 'ready':
        return '#10b981'; // green
      case 'not ready':
        return '#ef4444'; // red
      default:
        return '#9ca3af'; // gray
    }
  };

  // Build tooltip content showing only required confirmations
  // Show "pending" status if no confirmationSummary exists yet
  const tooltipItems: Array<{ label: string; status: string }> = [];

  if (pickupRequired) {
    const pickupStatus = confirmationSummary?.pickup?.status;
    tooltipItems.push({
      label: 'Pickup:',
      status: pickupStatus || 'pending',
    });
  }

  if (deliveryRequired) {
    const deliveryStatus = confirmationSummary?.delivery?.status;
    tooltipItems.push({
      label: 'Delivery:',
      status: deliveryStatus || 'pending',
    });
  }

  // Don't show tooltip if no items to display
  if (tooltipItems.length === 0) {
    return children;
  }

  const tooltipContent = (
    <Box sx={{ p: 1 }}>
      <Typography variant='caption' sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
        Confirmation Status
      </Typography>
      {tooltipItems.map((item, index) => (
        <Box
          key={item.label}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: index < tooltipItems.length - 1 ? 0.5 : 0,
          }}
        >
          <Typography variant='caption'>{item.label}</Typography>
          <Box
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: 1,
              backgroundColor: `${getStatusColor(item.status)}20`,
              color: getStatusColor(item.status),
              fontWeight: 500,
              fontSize: '0.7rem',
            }}
          >
            {formatStatusLabel(item.status)}
          </Box>
        </Box>
      ))}
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} placement='top' arrow enterDelay={200} leaveDelay={0}>
      {children}
    </Tooltip>
  );
};

export default ConfirmationStatusTooltip;
